import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type Scenario,
  type CreateScenarioDto,
  type UpdateScenarioDto,
  API_ROUTES,
  interpolateRoute,
} from '@super/shared-types'

/** Request body for create (companyId comes from session or query) */
type CreateScenarioBody = Omit<CreateScenarioDto, 'companyId'>
import { withErrorHandling } from '../utils'

export class ScenariosApiClient extends BaseApiClient {
  async getScenarios(): Promise<Scenario[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario[]>>(
          API_ROUTES.SCENARIOS.GET_ALL,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching scenarios',
      },
    )
    return result ?? []
  }

  async getScenarioById(id: string): Promise<Scenario | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario>>(
          interpolateRoute(API_ROUTES.SCENARIOS.GET, { id }),
        )
        return response.data ?? null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching scenario',
      },
    )
  }

  async createScenario(dto: CreateScenarioBody): Promise<Scenario | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario>>(
          API_ROUTES.SCENARIOS.CREATE,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      {
        errorContext: 'Error creating scenario',
      },
    )
  }

  async updateScenario(
    id: string,
    dto: UpdateScenarioDto,
  ): Promise<Scenario | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario>>(
          interpolateRoute(API_ROUTES.SCENARIOS.UPDATE, { id }),
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      {
        errorContext: 'Error updating scenario',
      },
    )
  }

  async deleteScenario(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request(
          interpolateRoute(API_ROUTES.SCENARIOS.DELETE, { id }),
          { method: 'DELETE' },
        )
        return true
      },
      {
        errorContext: 'Error deleting scenario',
      },
    )
    return result === true
  }

  /** [ADMIN] Get all scenarios for a company */
  async getScenariosForCompanyByAdmin(companyId: string): Promise<Scenario[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario[]>>(
          `${API_ROUTES.ADMIN.SCENARIOS.GET_BY_COMPANY}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error fetching scenarios for company ${companyId}`,
      },
    )
    return result ?? []
  }

  /** [ADMIN] Get scenario by ID for a company (use when super admin editing) */
  async getScenarioForCompanyByAdmin(
    id: string,
    companyId: string,
  ): Promise<Scenario | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario>>(
          `${interpolateRoute(API_ROUTES.ADMIN.SCENARIOS.GET, { id })}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching scenario',
      },
    )
  }

  /** [ADMIN] Create scenario for a company */
  async createScenarioForCompany(
    companyId: string,
    dto: CreateScenarioBody,
  ): Promise<Scenario | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario>>(
          `${API_ROUTES.ADMIN.SCENARIOS.CREATE}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating scenario' },
    )
  }

  /** [ADMIN] Update scenario */
  async updateScenarioForCompany(
    id: string,
    companyId: string,
    dto: UpdateScenarioDto,
  ): Promise<Scenario | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Scenario>>(
          `${interpolateRoute(API_ROUTES.ADMIN.SCENARIOS.UPDATE, { id })}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error updating scenario' },
    )
  }

  /** [ADMIN] Delete scenario */
  async deleteScenarioForCompany(
    id: string,
    companyId: string,
  ): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request(
          `${interpolateRoute(API_ROUTES.ADMIN.SCENARIOS.DELETE, { id })}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'DELETE' },
        )
        return true
      },
      { errorContext: 'Error deleting scenario' },
    )
    return result === true
  }
}

export const scenariosApiClient = new ScenariosApiClient()
